<template>
  <b-modal
    v-model="show"
    id="discountCodeModal"
    centered
    :title="`${ actionDisplay } Discount Code`"
    @shown="open"
    @hidden="clear"
  >
    <b-form class="create-tax-form">
      <info-row class="info-main-row" :title="'Name'">
        <b-col class="input-group">
          <b-form-input
            v-model="discountName"
            class="form-control"
            type="text"
            required
            :state="fieldState.discountName"
          />
        </b-col>
      </info-row>

      <info-row class="info-main-row" :title="'Description'">
        <b-col class="input-group">
          <b-form-textarea
            v-model="description"
            id="description"
            rows="2"
            type="text"
          />
        </b-col>
      </info-row>

      <info-row class="info-main-row" :title="'Code'">
        <b-col class="input-group">
          <b-form-input
            v-model="discountCode"
            class="form-control"
            type="text"
            required
            :state="fieldState.discountCode"
          />
        </b-col>
      </info-row>

      <info-row class="info-main-row" :title="'Type'">
        <b-col class="input-group type-select">
          <b-form-select
            v-model="discountType"
            required
            :state="fieldState.discountType"
            :options="discountTypes"
          />
        </b-col>
      </info-row>

      <info-row class="info-main-row" :title="'Rate'">
        <currency-input
          v-if="show"
          ref="rateInput"
          name="Default Rate"
          :currencySymbol="discountType === rateTypes.FIXED ? '$' : '%'"
          :amount.sync="discountRate"
          :fieldState="fieldState.discountRate"
        />
      </info-row>

      <info-row class="info-main-row" title="Enable">
        <toggle-button
          v-model="discountEnabled"
          class="active-toggle ml-3"
          :sync="true"
          :labels="{ checked: 'Yes', unchecked: 'No' }"
          :width="100"
          :height="33"
          color="#82C7EB"
          style="font-size: 16px;"
        />
      </info-row>
    </b-form>

    <div slot="modal-footer" class="w-100">
      <span class="text-danger">{{ warningText }}</span>
      <b-btn v-if="discount.id" class="float-left" variant="danger" @click="deleteDiscountCode">
        Delete
      </b-btn>
      <b-btn class="float-right" variant="success" @click="triggerDiscountCode">
        {{ actionDisplay }}
      </b-btn>
    </div>

    <div v-if="isRequesting" class="spinner-loading">
      <spinner-loader />
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import {
  PAYMENT_TYPES,
  PAYMENT_METHODS,
  MODIFIER_TYPES,
  MODIFIER_RATE_TYPES,
} from '@/config/constants'

import AlertMixin from '@/mixins/alert-mixin';

export default {
  name: 'DiscountCodeModal',

  components: {
    CurrencyInput: () => import('@/components/widgets/CurrencyInput'),
    InfoRow: () => import('@/components/InfoRow'),
    PicturePlaceholder: () => import('@/components/widgets/PicturePlaceholder'),
    SpinnerLoader: () => import('@/components/widgets/SpinnerLoader'),
    Taggregator: () => import('@/components/Taggregator'),
  },

  mixins: [
    AlertMixin
  ],

  data() {
    return {
      discount: {},
      actionDisplay: 'Create',
      show: null,
      discountName: '',
      discountCode: '',
      discountType: null,
      discountRate: '',
      discountEnabled: true,
      description: null,
      rateTypes: MODIFIER_RATE_TYPES,
      warningText: "",
      hideAfter: true,
      fieldState: { //true is valid, false shows the red invalid
        discountRate: null,
        discountName: null,
        discountCode: null,
        discountType: null
      }
    }
  },

  computed: {
    ...mapState({
      isRequesting: state => state.tags.isRequesting
    }),

    discountTypes() {
      return Object.keys(MODIFIER_RATE_TYPES)
        .map(discountType => {
          return {
            value: MODIFIER_RATE_TYPES[discountType],
            text: discountType.toLowerCase()
          }
        })
    }
  },

  methods: {
    ...mapActions([
    ]),

    open(discount = {}) {
      if (!discount.id) {
        return //for some reason, the button event gets called here sometimes.
      }

      this.discount = discount;
      this.actionDisplay = 'Update';

      this.discountName = discount.name;
      this.discountCode = discount.code;
      this.description = discount.description;
      this.discountRate = parseFloat(discount.rate);
      this.discountType = discount.type_id;
      this.discountEnabled = !!discount.is_enabled;

      this.show = true
    },

    triggerDiscountCode() {
      this.fieldState.discountName = !!this.discountName && this.discountName.length > 1
      this.fieldState.discountCode = !!this.discountCode && this.discountCode.length > 1
      this.fieldState.discountType = !!this.discountType
      this.fieldState.discountRate = !!this.discountRate || this.discountRate === 0

      if (
        this.fieldState.discountName &&
        this.fieldState.discountCode &&
        this.fieldState.discountRate &&
        this.fieldState.discountType
      ) {
        this.submitDiscountCode();
      } else {
        this.warningText = 'Name, Default Rate, Code and discountType are all required.'
      }
    },

    submitDiscountCode() {
      const params = {
        id: this.discount.id || 0,
        name: this.discountName,
        code: this.discountCode,
        description: this.description,
        rate: this.discountRate,
        type_id: this.discountType,
        is_enabled: this.discountEnabled ? 1 : 0
      }

      this.$emit('submit', params)

      if (true === this.hideAfter) {
        this.clear()
        this.show = false;
      }
    },

    async deleteDiscountCode() {
      if (this.discount.is_used) {
        this.errorNotification('This item is used for the bookings, so it can not be deleted');
        return;
      }

      let confirm_message = 'Are you sure?';

      if (this.discount.is_used) {
        confirm_message = `Are you sure you want to delete this discount code?`;
      }

      this.showConfirm(confirm_message)
        .then(result => {
          if (!result.value) {
            return;
          }

          this.$emit('delete', this.discount);

          if (true === this.hideAfter) {
            this.clear()
            this.show = false;
          }
        });
    },

    clear() {
      Object.assign(this.$data, this.$options.data.apply(this))
    }
  }
};
</script>

<style lang="scss">
#discountCodeModal {
  top: 30px;
}

.spinner-loading {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0px;
  top: 0px;
  background: rgba(255,255,255,0.7);
}

.input-group.type-select.col select {
  text-transform: capitalize;
}
</style>